import { get, post } from './http';

export type InteractionTargetType = 'solution' | 'tool';

export interface FavoriteItemDto {
  type: InteractionTargetType;
  id: string;
  addedAt: string | null;
}

export interface FavoritesResponse {
  items: FavoriteItemDto[];
  solutionIds: string[];
  toolIds: string[];
}

export interface FavoriteStatusResponse {
  targetType: InteractionTargetType;
  targetId: string;
  isCollected: boolean;
}

export interface LikeStatusResponse {
  targetType: InteractionTargetType;
  targetId: string;
  likeCount: number;
  isLiked: boolean;
  rewardCount: number;
  maxRewardLikes: number;
  canEarnReward: boolean;
  featureEnabled: boolean;
}

export interface PublicLikeStatusResponse {
  targetType: InteractionTargetType;
  targetId: string;
  likeCount: number;
  featureEnabled: boolean;
}

export interface ToggleLikeResponse extends LikeStatusResponse {
  rewarded: boolean;
  pointsAwarded: number;
  message: string;
}

export interface ShareRecordResponse {
  targetType: InteractionTargetType;
  targetId: string;
  rewarded: boolean;
  pointsAwarded: number;
  rewardCount: number;
  maxRewardShares: number;
  canEarnReward: boolean;
  message: string;
}

export interface CheckinStatusResponse {
  checkedToday: boolean;
  streakDays: number;
  totalDays: number;
  lastCheckinDate: string | null;
  todayReward: number;
  featureEnabled: boolean;
}

export interface CheckinDoResponse {
  checkedToday: true;
  streakDays: number;
  totalDays: number;
  pointsAwarded: number;
  message: string;
}

export interface LoginRewardResponse {
  claimed: boolean;
  pointsAwarded: number;
  totalPoints?: number;
  message: string;
}

export type AttachmentType = 'solution_attachment' | 'tool_attachment';
export type AttachmentResourceType = 'solution' | 'tool';

export interface AttachmentPurchaseResponse {
  alreadyPurchased: boolean;
  pointsDeducted: number;
  remainingPoints: number;
  message: string;
}

export interface PurchasedAttachmentsResponse {
  attachmentIds: string[];
}

export interface DownloadRecordDto {
  id: string;
  targetType: InteractionTargetType;
  targetId: string;
  toolId: string | null;
  toolName: string | null;
  toolCategory: string | null;
  version: string | null;
  size: string | null;
  downloadUrl: string | null;
  platform: string[];
  downloadedAt: string | null;
}

export interface PageViewRecordResponse {
  targetType: InteractionTargetType;
  targetId: string;
  viewCount: number;
}

export interface BrowsingHistoryItemDto {
  id: string;
  type: InteractionTargetType;
  title: string | null;
  image: string | null;
  category: string | null;
  visitedAt: string | null;
}

export function getMyFavorites(params?: { targetType?: InteractionTargetType }) {
  return get<FavoritesResponse>('/interactions/favorites', { params });
}

export function getFavoriteStatus(params: { targetType: InteractionTargetType; targetId: string | number }) {
  return get<FavoriteStatusResponse>('/interactions/favorites/status', { params });
}

export function toggleFavorite(payload: { targetType: InteractionTargetType; targetId: string | number }) {
  return post<FavoriteStatusResponse>('/interactions/favorites/toggle', payload);
}

export function removeFavorite(payload: { targetType: InteractionTargetType; targetId: string | number }) {
  return post<{ targetType: InteractionTargetType; targetId: string; removed: boolean }>(
    '/interactions/favorites/remove',
    payload,
  );
}

export function getPublicLikeStatus(params: { targetType: InteractionTargetType; targetId: string | number }) {
  return get<PublicLikeStatusResponse>('/public/interactions/likes/status', { params });
}

export function getMyLikeStatus(params: { targetType: InteractionTargetType; targetId: string | number }) {
  return get<LikeStatusResponse>('/interactions/likes/status', { params });
}

export function toggleLike(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
  targetTitle?: string;
}) {
  return post<ToggleLikeResponse>('/interactions/likes/toggle', payload);
}

export function recordShare(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
  targetTitle?: string;
  channel?: 'copy_link' | 'wechat' | 'qq' | 'weibo' | 'other';
}) {
  return post<ShareRecordResponse>('/interactions/shares/record', payload);
}

export function getCheckinStatus() {
  return get<CheckinStatusResponse>('/interactions/checkin/status');
}

export function doCheckin() {
  return post<CheckinDoResponse>('/interactions/checkin/do');
}

export function claimLoginReward() {
  return post<LoginRewardResponse>('/interactions/login-reward/claim');
}

export function purchaseAttachmentByPoints(payload: {
  attachmentType: AttachmentType;
  attachmentId: string | number;
  resourceType: AttachmentResourceType;
  resourceId: string | number;
}) {
  return post<AttachmentPurchaseResponse>('/interactions/attachments/purchase', payload);
}

export function getPurchasedAttachments(params: {
  attachmentType: AttachmentType;
  resourceType: AttachmentResourceType;
  resourceId: string | number;
}) {
  return get<PurchasedAttachmentsResponse>('/interactions/attachments/purchases', { params });
}

export function getDownloadRecords(params?: { limit?: number }) {
  return get<DownloadRecordDto[]>('/interactions/downloads', { params });
}

export function recordDownload(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
  toolId?: string | number;
  fileObjectId?: string | number;
  sourceType: 'tool_attachment' | 'solution_attachment' | 'file_center';
  platformSnapshot?: string | string[];
  versionSnapshot?: string;
  downloadUrlSnapshot?: string;
}) {
  return post<{ recorded: boolean }>('/interactions/downloads/record', payload);
}

export function removeDownloadRecord(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
}) {
  return post<{ removed: boolean }>('/interactions/downloads/remove', payload);
}

export function recordPageView(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
}) {
  return post<PageViewRecordResponse>('/public/interactions/views/record', payload);
}

export function getBrowsingHistory(params?: {
  targetType?: InteractionTargetType;
  limit?: number;
}) {
  return get<BrowsingHistoryItemDto[]>('/interactions/history', { params });
}

export function recordBrowsingHistory(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
  titleSnapshot?: string;
  imageUrl?: string;
  categorySnapshot?: string;
}) {
  return post<{ recorded: boolean }>('/interactions/history/record', payload);
}

export function removeBrowsingHistory(payload: {
  targetType: InteractionTargetType;
  targetId: string | number;
}) {
  return post<{ removed: boolean }>('/interactions/history/remove', payload);
}

export function clearBrowsingHistory() {
  return post<{ cleared: boolean }>('/interactions/history/clear');
}

export interface MemberStatsResponse {
  totalFavoritesCount: number;
  totalDownloadsCount: number;
  currentPoints: number;
}

export function getMemberStats() {
  return get<MemberStatsResponse>('/interactions/stats');
}
